import React, { useState } from "react";
import { useProgress } from "../context/ProgressContext";

const PART_COLORS = [
  { bg: "#0ea5e9", light: "#e0f2fe", border: "#bae6fd", text: "#0369a1" },
  { bg: "#7c3aed", light: "#ede9fe", border: "#c4b5fd", text: "#5b21b6" },
  { bg: "#059669", light: "#d1fae5", border: "#6ee7b7", text: "#065f46" },
  { bg: "#d97706", light: "#fef3c7", border: "#fcd34d", text: "#92400e" },
  { bg: "#db2777", light: "#fce7f3", border: "#f9a8d4", text: "#9d174d" },
  { bg: "#0891b2", light: "#cffafe", border: "#67e8f9", text: "#164e63" },
];

function QuizPart({ part, partIndex, globalOffset, answers, submitted, onAnswer }) {
  const color = PART_COLORS[partIndex % PART_COLORS.length];
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{
        background: color.bg, padding: "10px 18px",
        fontSize: 12, fontWeight: 700, color: "#fff", letterSpacing: 0.5,
        textTransform: "uppercase",
      }}>
        {part.label}
      </div>
      <div style={{ padding: "16px 18px 4px" }}>
        {part.questions.map((q, i) => {
          const globalIdx = globalOffset + i;
          const chosen = answers[globalIdx];
          const correct = q.correct;
          return (
            <div key={i} style={{ marginBottom: 20 }}>
              <p style={{ fontWeight: 600, color: "#1e293b", fontSize: 14, marginBottom: 10 }}>
                {globalOffset + i + 1}. {q.question}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {q.options.map((opt, j) => {
                  let bg = "#f8fafc", border = "#e2e8f0", clr = "#374151";
                  if (submitted) {
                    if (j === correct) { bg = "#f0fdf4"; border = "#86efac"; clr = "#14532d"; }
                    else if (j === chosen && j !== correct) { bg = "#fef2f2"; border = "#fca5a5"; clr = "#7f1d1d"; }
                  } else if (chosen === j) {
                    bg = color.light; border = color.border; clr = color.text;
                  }
                  return (
                    <button key={j} disabled={submitted}
                      onClick={() => onAnswer(globalIdx, j)}
                      style={{
                        textAlign: "left", padding: "9px 12px",
                        background: bg, border: `1px solid ${border}`,
                        borderRadius: 6, color: clr, fontSize: 13,
                        cursor: submitted ? "default" : "pointer", transition: "all 0.1s",
                        fontFamily: "inherit",
                      }}
                    >
                      {opt}
                      {submitted && j === correct && <span style={{ float: "right", fontWeight: 700 }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Quiz({ quiz, lessonId }) {
  const { markComplete, isComplete } = useProgress();
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const done = isComplete(lessonId);

  // Support both old flat format and new parts format
  const parts = quiz.parts
    ? quiz.parts
    : [{ label: quiz.title || "Questions", questions: quiz.questions }];

  const allQuestions = parts.flatMap(p => p.questions);
  const totalQ = allQuestions.length;
  const score = submitted
    ? allQuestions.filter((q, i) => answers[i] === q.correct).length
    : 0;

  const handleSubmit = () => {
    if (Object.keys(answers).length < totalQ) return;
    setSubmitted(true);
    if (score === totalQ) markComplete(lessonId);
  };

  let offset = 0;

  return (
    <div style={{ marginTop: 28, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
      <div style={{
        background: "#1e293b", padding: "12px 18px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <h4 style={{ color: "#fff", fontWeight: 800, fontSize: 15, margin: 0 }}>
          {quiz.title}
        </h4>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: "#94a3b8", fontSize: 12 }}>{totalQ} questions</span>
          {done && (
            <span style={{ color: "#fff", fontSize: 12, background: "#16a34a", padding: "2px 8px", borderRadius: 10 }}>
              ✓ Completed
            </span>
          )}
        </div>
      </div>

      {parts.map((part, pi) => {
        const el = (
          <QuizPart
            key={pi}
            part={part}
            partIndex={pi}
            globalOffset={offset}
            answers={answers}
            submitted={submitted}
            onAnswer={(idx, j) => setAnswers(a => ({ ...a, [idx]: j }))}
          />
        );
        offset += part.questions.length;
        return el;
      })}

      <div style={{ padding: "0 18px 18px" }}>
        {!submitted ? (
          <button
            onClick={handleSubmit}
            disabled={Object.keys(answers).length < totalQ}
            style={{
              padding: "10px 28px",
              background: Object.keys(answers).length < totalQ ? "#94a3b8" : "#0ea5e9",
              border: "none", borderRadius: 6, color: "#fff",
              fontSize: 14, fontWeight: 700,
              cursor: Object.keys(answers).length < totalQ ? "not-allowed" : "pointer",
              fontFamily: "inherit",
            }}
          >
            Submit answers ({Object.keys(answers).length}/{totalQ} answered)
          </button>
        ) : (
          <div style={{
            background: score === totalQ ? "#f0fdf4" : "#fffbeb",
            border: `1px solid ${score === totalQ ? "#86efac" : "#fde68a"}`,
            borderRadius: 6, padding: "14px 18px",
          }}>
            <p style={{ fontWeight: 800, color: score === totalQ ? "#16a34a" : "#d97706", margin: "0 0 4px", fontSize: 15 }}>
              {score}/{totalQ} correct{score === totalQ ? " — Section complete! ✓" : ""}
            </p>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 8px" }}>
              {score === totalQ
                ? "Excellent work. You have demonstrated a solid understanding of this section."
                : `You got ${totalQ - score} question${totalQ - score > 1 ? "s" : ""} wrong. Review the relevant lesson and try again.`}
            </p>
            {score < totalQ && (
              <button
                onClick={() => { setSubmitted(false); setAnswers({}); }}
                style={{ background: "none", border: "none", color: "#0ea5e9", fontSize: 13, cursor: "pointer", padding: 0, fontWeight: 700, fontFamily: "inherit" }}
              >
                Try again →
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}